import React, { useState, useEffect, useRef } from "react";
import ServicesPage from "./ServicesPage";
import AboutPage from "./AboutPage";
import CapabilitiesPage from "./CapabilitiesPage";
import BookingSection from "./BookingSection";
import { HashRouter as Router, Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import ScrollToTop from "./ScrollToTop";
import { Helmet } from "react-helmet";
import { LangProvider, useLang, Bi } from "./lang";

// New JUMIBI Corporate Identity (Deep Tech Blue). This replaces the legacy
// App.css / brand-v2.css layers. Tailwind (index.css) stays for the consent banner.
import "./jumibi-ci.css";

export { Header, Footer, CTA, Clarity };

/* ============================================================ Brand mark */
export function PyramidMark() {
  // 3-tier JUMIBI mark — matches the live products (orderintake) and Brand.js.
  return (
    <svg className="mark" viewBox="0 0 100 100" aria-hidden="true">
      <path fill="#0369A1" d="M 22 60 L 78 60 L 90 88 L 10 88 Z" />
      <path fill="#0EA5E9" d="M 32 32 L 68 32 L 76 56 L 24 56 Z" />
      <path fill="#00D1FF" d="M 42 8 L 58 8 L 64 28 L 36 28 Z" />
    </svg>
  );
}

// Crisp text lockup for the dark module banners — replaces the small PNG strips.
// "▲ JUMIBI | <module>" in real type, fully legible at any size.
export function ModuleLockup({ code, large = false }) {
  return (
    <span className={"jb-lockup" + (large ? " jb-lockup--lg" : "")}>
      <PyramidMark />
      <span className="jb-lockup__wm">JUMIBI</span>
      {code && <span className="jb-lockup__code">{code}</span>}
    </span>
  );
}

/* ============================================================ Hash-router link fix
   The app uses HashRouter (good for static hosting), so the URL hash is owned by
   the router (#/route). In-page anchors like href="/#book" or "#platform" would
   otherwise collide with routing. This interceptor catches those clicks, routes
   to the home page if needed, and smooth-scrolls to the target section by id. */
function HashScrollFix() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  useEffect(() => {
    const onClick = (e) => {
      const a = e.target.closest && e.target.closest("a");
      if (!a) return;
      const href = a.getAttribute("href") || "";
      const m = href.match(/^\/?#(.+)$/);
      if (!m) return;
      const id = m[1];
      if (id.startsWith("/")) return; // router-style hash (#/route) — leave it
      e.preventDefault();
      const doScroll = () => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      };
      if (pathname !== "/") {
        navigate("/");
        window.setTimeout(doScroll, 400); // wait for the home page to render
      } else {
        doScroll();
      }
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [navigate, pathname]);
  return null;
}

/* ============================================================ Head / meta */
function HeadMeta() {
  return (
    <Helmet>
      <html lang="de" />
      <title>JUMIBI — The AI process layer for multi-ERP portfolios</title>
      <meta
        name="description"
        content="AI-Automatisierung pro Standort. Plattform-Konsolidierung für das Portfolio. Live in Wochen, nicht in Jahren."
      />
      <meta name="theme-color" content="#0A192F" />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="JUMIBI" />
      <meta property="og:locale" content="de_DE" />
      <meta property="og:url" content="https://www.jumibi.de/" />
      <meta property="og:title" content="JUMIBI AI Business Suite" />
      <meta
        property="og:description"
        content="Die KI-Prozess-Schicht für Multi-ERP-Portfolios. AP, Capture, E-Invoicing, Archive & Order Intake — eine Plattform, ein Login, jedes ERP unangetastet."
      />
      <meta property="og:image" content="https://www.jumibi.de/og-card.png" />
      <meta property="og:image:secure_url" content="https://www.jumibi.de/og-card.png" />
      <meta property="og:image:type" content="image/png" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta
        property="og:image:alt"
        content="JUMIBI AI Business Suite — Die KI-Prozess-Schicht für Multi-ERP-Portfolios"
      />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="JUMIBI AI Business Suite" />
      <meta
        name="twitter:description"
        content="Die KI-Prozess-Schicht für Multi-ERP-Portfolios. AP, Capture, E-Invoicing, Archive & Order Intake — eine Plattform, ein Login, jedes ERP unangetastet."
      />
      <meta name="twitter:image" content="https://www.jumibi.de/og-card.png" />
    </Helmet>
  );
}

function Clarity() {
  useEffect(() => {
    (function (c, l, a, r, i, t, y) {
      c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments); };
      t = l.createElement(r); t.async = 1; t.src = "https://www.clarity.ms/tag/" + i;
      y = l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t, y);
    })(window, document, "clarity", "script", "tie2eg76cw");
  }, []);
  return null;
}

function JsonLd() {
  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.innerHTML = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "JUMIBI — AI process layer for multi-ERP portfolios",
      url: "https://www.jumibi.de",
      logo: "https://www.jumibi.de/brand/mark.svg",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      description:
        "JUMIBI is the AI process layer for multi-ERP portfolios. AI automation per site, platform consolidation across the portfolio. Live in weeks, not years.",
      offers: {
        "@type": "Offer",
        price: "Contact for pricing",
        priceCurrency: "EUR",
        url: "https://www.jumibi.de/#book",
      },
    });
    document.head.appendChild(script);
    return () => { document.head.removeChild(script); };
  }, []);
  return null;
}

/* ============================================================ Consent banner */
function ConsentBanner({ onAccept, onDecline }) {
  const { lang } = useLang();
  const texts = {
    de: {
      title: "Erleben Sie die Website in voller Funktionalität!",
      message:
        "Nur mit Ihrer Zustimmung zu Microsoft Clarity können wir die Website für Sie optimal darstellen, Ladezeiten minimieren und Inhalte personalisieren. Ihre Daten bleiben anonym.",
      accept: "Jetzt erleben",
      decline: "Später",
    },
    en: {
      title: "Unlock the full experience!",
      message:
        "Only by consenting to Microsoft Clarity can we optimize the website for you, reduce loading times, and personalize content. Your data remains anonymous.",
      accept: "Experience Now",
      decline: "Later",
    },
  };
  const t = texts[lang];
  return (
    <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-white border border-gray-300 rounded-xl shadow-lg p-6 max-w-3xl w-[90%] flex flex-col md:flex-row justify-between items-center z-50 space-y-4 md:space-y-0 md:space-x-6 animate-slideUp">
      <div className="text-gray-800 text-center md:text-left">
        <p className="font-medium mb-1">{t.title}</p>
        <p className="text-gray-600 text-sm">{t.message}</p>
      </div>
      <div className="flex space-x-3">
        <button onClick={onAccept} className="bg-blue-600 text-white px-5 py-2 rounded-xl shadow hover:bg-blue-700 transition">
          {t.accept}
        </button>
        <button onClick={onDecline} className="bg-gray-200 text-gray-800 px-5 py-2 rounded-xl shadow hover:bg-gray-300 transition">
          {t.decline}
        </button>
      </div>
    </div>
  );
}

/* ============================================================ Header */
const NAV = [
  { key: "platform", to: "/#platform", section: true, en: "Platform", de: "Platform" },
  { key: "capabilities", to: "/capabilities", section: false, en: "Capabilities", de: "Capabilities" },
  { key: "industries", to: "/#industries", section: true, en: "Industries", de: "Industries" },
  { key: "pe", to: "/#for-pe", section: true, en: "For PE firms", de: "Für PE-Häuser" },
  { key: "about", to: "/about", section: false, en: "About", de: "Über uns" },
];

function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { lang, setLang } = useLang();
  const { pathname } = useLocation();

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const close = () => setMobileOpen(false);

  return (
    <>
      <header className="site-header" role="banner">
        <div className="container header-inner">
          <Link to="/" className="brand" aria-label="JUMIBI">
            <PyramidMark />
            <span className="wm">JUMIBI</span>
          </Link>

          <nav className="main-nav" aria-label="Main navigation">
            <ul>
              {NAV.map((item) => (
                <li key={item.key}>
                  {item.section ? (
                    <a href={item.to} className={pathname === "/" && item.key === "platform" ? "active" : ""}>
                      {lang === "de" ? item.de : item.en}
                    </a>
                  ) : (
                    <Link to={item.to} className={pathname === item.to ? "active" : ""}>
                      {lang === "de" ? item.de : item.en}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </nav>

          <div className="header-actions">
            <div className="lang-toggle" role="group" aria-label="Language">
              <button type="button" className={lang === "en" ? "active" : ""} onClick={() => setLang("en")}>EN</button>
              <button type="button" className={lang === "de" ? "active" : ""} onClick={() => setLang("de")}>DE</button>
            </div>
            <a href="/#book" className="btn btn--primary" style={{ padding: "11px 18px", fontSize: 13 }}>
              <Bi en="Book a demo" de="Demo buchen" />
            </a>
            <button
              className="mobile-toggle"
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen((v) => !v)}
            >
              <span className="hamburger" />
            </button>
          </div>
        </div>
      </header>

      <div className={"mobile-menu" + (mobileOpen ? " open" : "")}>
        <button className="close" aria-label="Close menu" onClick={close}>&times;</button>
        {NAV.map((item) =>
          item.section ? (
            <a key={item.key} href={item.to} onClick={close}>{lang === "de" ? item.de : item.en}</a>
          ) : (
            <Link key={item.key} to={item.to} onClick={close}>{lang === "de" ? item.de : item.en}</Link>
          )
        )}
        <a href="/#book" onClick={close}><Bi en="Book a demo" de="Demo buchen" /></a>
      </div>
    </>
  );
}

/* ============================================================ Hero */
function Hero() {
  return (
    <section className="hero" id="top">
      <div className="container hero-grid">
        <div>
          <span className="eyebrow eyebrow--dark">
            <Bi en="The AI process layer for multi-ERP portfolios" de="Die KI-Prozess-Schicht für Multi-ERP-Portfolios" />
          </span>
          <h1 className="h1">
            <Bi
              en={<>Consolidate processes.<br /><span className="accent">Not ERPs.</span></>}
              de={<>Konsolidiert Prozesse.<br /><span className="accent">Nicht ERPs.</span></>}
            />
          </h1>
          <p className="sub">
            <Bi
              en="AI automation per site. Platform consolidation across the portfolio. Live in weeks, not years."
              de="KI-Automatisierung pro Standort. Plattform-Konsolidierung für das Portfolio. Live in Wochen, nicht in Jahren."
            />
            <span className="mono-line">{"// existing systems, new capabilities"}</span>
          </p>
          <div className="hero-cta">
            <a href="/#book" className="btn btn--primary">
              <Bi en="Book a 30-min demo" de="30-Min-Demo buchen" />
            </a>
            <a href="#platform" className="btn btn--outline">
              <Bi en="See the platform" de="Plattform ansehen" />
            </a>
          </div>
          <div className="hero-meta">
            <div className="m"><b>2–4<span style={{ fontSize: 18 }}> wks</span></b><span><Bi en="Per-site roll-out" de="Roll-out pro Standort" /></span></div>
            <div className="m"><b>5</b><span><Bi en="Live modules" de="Live-Module" /></span></div>
            <div className="m"><b>€55M</b><span><Bi en="Invoice volume / 30-day cycle" de="Rechnungsvolumen / 30 Tage" /></span></div>
          </div>
        </div>

        <div className="arch" aria-label="Platform architecture">
          <div className="layer-label"><Bi en="Modules · functional capabilities" de="Module · funktionale Capabilities" /></div>
          <div className="modules-row">
            <div className="mod"><span>Accounts Payable</span></div>
            <div className="mod"><span>Capture</span></div>
            <div className="mod"><span>E-Invoicing</span></div>
            <div className="mod"><span>Archive</span></div>
            <div className="mod"><span>Order Intake</span></div>
          </div>
          <div className="conn">▼</div>
          <div className="platform-bar">
            JUMIBI AI Business Suite
            <small><Bi en="AI process layer · portfolio visibility · multi-tenant" de="KI-Prozess-Schicht · Portfolio-Sichtbarkeit · Multi-Tenant" /></small>
          </div>
          <div className="conn">▲</div>
          <div className="layer-label"><Bi en="Your ERP landscape — untouched" de="Ihre ERP-Landschaft — unangetastet" /></div>
          <div className="erp-row">
            <div className="erp">SAP</div>
            <div className="erp">ProAlpha</div>
            <div className="erp">Dynamics</div>
            <div className="erp">Infor</div>
            <div className="erp">Oracle</div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================================================ Trust marquee */
const CUSTOMER_LOGOS = [
  "mutares-1.jpg", "Matikon-1.jpg", "NEM-1.png", "Fasana-1.png",
  "EFACEC-1.jpg", "SFC-1.png", "Consol-1.png", "HILO-Group-1.webp",
];

function TrustBar() {
  return (
    <section className="section--sm section--steel">
      <div className="container">
        <p className="caption" style={{ color: "var(--on-dark-mut)", marginBottom: 28, textAlign: "center" }}>
          <Bi
            en="In production at 10+ industrial companies across PE portfolios"
            de="Im Einsatz bei 10+ Industriegesellschaften in PE-Portfolios"
          />
        </p>
        <div className="marquee">
          <div className="marquee-track">
            {[...CUSTOMER_LOGOS, ...CUSTOMER_LOGOS].map((file, i) => (
              <img key={i} src={`${process.env.PUBLIC_URL}/images/customers/${file}`} alt="" loading="lazy" />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================================================ Platform value stack */
function PlatformStack() {
  return (
    <section className="section" id="platform">
      <div className="container">
        <div className="section-head">
          <span className="eyebrow"><Bi en="The platform" de="Die Plattform" /></span>
          <h2 className="h2" style={{ marginTop: 14 }}>
            <Bi en="Three stages of value. One platform." de="Drei Wertstufen. Eine Plattform." />
          </h2>
          <p>
            <Bi
              en="Each stage carries its own ROI. Stage 1 is the entry lever — what Operating Partners and CFOs buy first. Stages 2 and 3 compound across the portfolio."
              de="Jede Stufe trägt einen eigenen ROI. Stufe 1 ist der Einstiegs-Hebel — was Operating Partner und CFOs zuerst kaufen. Stufe 2 und 3 entfalten ihre Wirkung über das Portfolio."
            />
          </p>
        </div>
        <div className="stack">
          <div className="stage">
            <div className="s-no">01<span className="lvl"><Bi en="ENTITY" de="STANDORT" /></span></div>
            <div>
              <h3 className="h3"><Bi en="AI automation at entity level" de="KI-Automatisierung auf Standort-Ebene" /></h3>
              <p>
                <Bi
                  en="The immediate lever. JUMIBI replaces legacy OCR tools like tangro and manual SAP processing with template-free AI extraction that adapts to each ERP. No template maintenance, no per-supplier configuration."
                  de="Der unmittelbare Hebel. JUMIBI ersetzt Legacy-OCR-Tools wie tangro und manuelle SAP-Bearbeitung durch Template-freie KI-Extraktion, die sich an jedes ERP anpasst. Keine Template-Pflege, keine Konfiguration pro Lieferant."
                />
              </p>
            </div>
            <div className="s-metric"><b>30–40%</b><span><Bi en="less manual effort per site" de="weniger manueller Aufwand / Standort" /></span></div>
          </div>

          <div className="stage">
            <div className="s-no">02<span className="lvl">PORTFOLIO</span></div>
            <div>
              <h3 className="h3"><Bi en="Process consolidation & portfolio visibility" de="Prozess-Konsolidierung & Portfolio-Sichtbarkeit" /></h3>
              <p>
                <Bi
                  en="Processes standardize across all entities while the underlying ERPs stay heterogeneous. One platform licence instead of scattered per-entity tools — and a cross-entity dashboard for the Holding CFO: volumes, throughput, approver wait times, backlogs, cost-per-invoice."
                  de="Prozesse werden über alle Entities standardisiert, während die ERPs heterogen bleiben. Eine Plattform-Lizenz statt verstreuter Einzel-Tools — plus ein Cross-Entity-Dashboard für den Holding-CFO: Volumina, Durchlaufzeiten, Approver-Wartezeiten, Backlogs, Cost-per-Invoice."
                />
              </p>
            </div>
            <div className="s-metric"><b>7-figure</b><span><Bi en="licence savings / yr" de="Lizenz-Einsparung / Jahr" /></span></div>
          </div>

          <div className="stage">
            <div className="s-no">03<span className="lvl">SSC</span></div>
            <div>
              <h3 className="h3"><Bi en="Shared Service Center on the platform" de="Shared-Service-Center auf der Plattform" /></h3>
              <p>
                <Bi
                  en="A central SSC handles back-office processes (AP, OE, Master Data) for multiple portfolio companies from one place — all on JUMIBI, ERPs untouched. Historically near-impossible without ERP harmonisation; here it follows as a logical consequence of the platform."
                  de="Eine zentrale SSC-Einheit bearbeitet Backoffice-Prozesse (AP, OE, Stammdaten) für mehrere Portfolio-Unternehmen aus einer Hand — alle auf JUMIBI, ERPs unangetastet. Historisch ohne ERP-Harmonisierung kaum möglich; hier folgt es als logische Konsequenz der Plattform."
                />
              </p>
            </div>
            <div className="s-metric"><b>30–50%</b><span><Bi en="back-office cost cut" de="Backoffice-Kosten" /></span></div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================================================ Capabilities (home) */
const HOME_MODULES = [
  {
    banner: "ap-dark.png", code: "AP", fn: "Accounts Payable", badge: "live",
    en: "AI invoice processing — template-free extraction, approval workflow, ERP posting. Live in production across 12 mailboxes and 12 teams (formerly DocInsight).",
    de: "KI-Eingangsrechnungsverarbeitung — Template-freie Extraktion, Approval-Workflow, ERP-Buchung. Live im Produktivbetrieb über 12 Mailboxen und 12 Teams (ehemals DocInsight).",
    foot: "Replaces tangro & manual AP",
  },
  {
    banner: "capture-dark.png", code: "Capture", fn: "Document Capture", badge: "live",
    en: "AI capture & structured filing of physical and digital documents — shop-floor papers, production docs, delivery notes. Live in production at two industrial sites.",
    de: "KI-Erfassung & strukturierte Ablage physischer und digitaler Belege — Shopfloor-Papiere, Produktionsdokumente, Lieferscheine. Live im Produktivbetrieb an zwei Industriestandorten.",
    foot: "No double data entry",
  },
  {
    banner: "einvoicing-dark.png", code: "E-Invoicing", fn: "E-Invoicing", badge: "live",
    en: "Electronic outbound invoicing with country compliance — Italian SDI, French PPF/PDP, Serbian E-Faktura. Live in Italy, France and Serbia; more countries scale without external specialists.",
    de: "Elektronische Ausgangsrechnung mit länderspezifischer Compliance — italienisches SDI, französisches PPF/PDP, serbisches E-Faktura. Live in Italien, Frankreich und Serbien; weitere Länder ohne externe Spezialanbieter.",
    foot: "Replaces €85k / 5 yrs per country",
  },
  {
    banner: "archive-dark.png", code: "Archive", fn: "ERP Archive", badge: "live",
    en: "Platform-wide ERP archive — audit-proof storage, full-text search, ERP integration over API.",
    de: "Plattform-weites ERP-Archiv — revisionssichere Ablage, Volltextsuche, ERP-Anbindung über API.",
    foot: "Audit-proof & searchable",
  },
  {
    banner: "orderintake-dark.png", code: "Order Intake", fn: "Order Intake", badge: "live",
    en: "AI order capture from incoming purchase orders (email, EDI, PDF) — automated creation in the ERP over API. It doesn't just speed up intake: it optimizes sales by surfacing up- and cross-sell opportunities, so you sell more products per order.",
    de: "KI-Auftragserfassung aus eingehenden Bestellungen (E-Mail, EDI, PDF) — automatisiertes Anlegen im ERP über API. Optimiert nicht nur den Auftragseingang, sondern auch den Vertrieb: erkennt Up- und Cross-Selling-Potenziale, um mehr Produkte pro Auftrag zu verkaufen.",
    foot: "Auftragseingang + Sales-Uplift",
  },
];

function Badge({ kind }) {
  if (kind === "live") return <span className="badge badge--live"><span className="dot" />Live</span>;
  if (kind === "dev") return <span className="badge badge--dev"><span className="dot" /><Bi en="In development" de="In Entwicklung" /></span>;
  if (kind === "poc") return <span className="badge badge--poc"><span className="dot" />POC</span>;
  return null;
}

function HomeCapabilities() {
  const { lang } = useLang();
  return (
    <section className="section section--ice" id="capabilities">
      <div className="container">
        <div className="section-head">
          <span className="eyebrow"><Bi en="The JUMIBI AI Business Suite" de="Die JUMIBI AI Business Suite" /></span>
          <h2 className="h2" style={{ marginTop: 14 }}>
            <Bi en="One AI Business Suite. Modules you switch on as you scale." de="Eine AI Business Suite. Module, die Sie beim Skalieren dazuschalten." />
          </h2>
          <p>
            <Bi
              en="The JUMIBI AI Business Suite bundles AP, Capture, E-Invoicing, Archive and Order Intake on one AI process layer. Start with a single module and add the rest as you grow — one platform, one login, one cross-entity dashboard, every ERP untouched underneath. The same AI also reads your portfolio data and recommends what to act on next."
              de="Die JUMIBI AI Business Suite bündelt AP, Capture, E-Invoicing, Archive und Order Intake auf einer KI-Prozess-Schicht. Starten Sie mit einem Modul und schalten Sie die übrigen beim Wachsen dazu — eine Plattform, ein Login, ein Cross-Entity-Dashboard, jedes ERP unangetastet darunter. Dieselbe KI liest Ihre Portfolio-Daten und empfiehlt, was als Nächstes zu tun ist."
            />
          </p>
        </div>

        <div className="grid grid-3">
          {HOME_MODULES.map((m) => (
            <article className="module-card" key={m.fn}>
              <div className="m-banner"><ModuleLockup code={m.code} /></div>
              <div className="m-body">
                <div className="m-top">
                  <span className="m-fn">{m.fn}</span>
                  <Badge kind={m.badge} />
                </div>
                <p>{lang === "de" ? m.de : m.en}</p>
                <div className="m-foot">{m.foot}</div>
              </div>
            </article>
          ))}

          <article className="roadmap-card">
            <div className="m-fn" style={{ margin: 0 }}><Bi en="On the roadmap" de="Auf der Roadmap" /></div>
            <div className="pill-list">
              <span className="pill">JUMIBI Master Data</span>
              <span className="pill">JUMIBI Procurement</span>
            </div>
          </article>
        </div>

        <div style={{ marginTop: 32 }}>
          <Link to="/capabilities" className="btn btn--ghost">
            <Bi en="Explore all capabilities →" de="Alle Capabilities ansehen →" />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ============================================================ AP video spotlight */
export function VideoCard() {
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef(null);
  const play = () => {
    setPlaying(true);
    const v = videoRef.current;
    if (v) { v.setAttribute("controls", ""); v.play().catch(() => {}); }
  };
  return (
    <div className="video-card">
      {!playing && <img className="poster" src={`${process.env.PUBLIC_URL}/images/video-poster.png`} alt="JUMIBI AP — AI invoice processing" />}
      <video
        ref={videoRef}
        src={`${process.env.PUBLIC_URL}/videos/DocInsightMarketingVideo-1.mp4`}
        poster={`${process.env.PUBLIC_URL}/images/video-poster.png`}
        preload="none"
        playsInline
        width="1280"
        height="720"
        style={{ display: playing ? "block" : "none" }}
      />
      {!playing && (
        <button className="play" aria-label="Play video" onClick={play}>
          <span><svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg></span>
        </button>
      )}
    </div>
  );
}

function APVideo() {
  return (
    <section className="section">
      <div className="container">
        <div className="split">
          <div>
            <span className="eyebrow"><Bi en="See it run · JUMIBI AP" de="In Aktion · JUMIBI AP" /></span>
            <h2 className="h2" style={{ marginTop: 14 }}>
              <Bi en="Watch AP process a real invoice" de="AP bei der Rechnungsverarbeitung" />
            </h2>
            <p className="lede" style={{ marginTop: 16 }}>
              <Bi
                en="From inbox to ERP posting — template-free extraction, validation and multi-level approval. The same engine running live in production across 12 mailboxes and 12 teams."
                de="Vom Posteingang bis zur ERP-Buchung — Template-freie Extraktion, Validierung und mehrstufige Freigabe. Dieselbe Engine, die live im Produktivbetrieb über 12 Mailboxen und 12 Teams läuft."
              />
            </p>
            <div className="hero-cta" style={{ marginTop: 28 }}>
              <Link to="/capabilities#ap" className="btn btn--ink"><Bi en="JUMIBI AP details" de="JUMIBI AP im Detail" /></Link>
              <a href="/#book" className="btn btn--ghost"><Bi en="Book a live demo →" de="Live-Demo buchen →" /></a>
            </div>
          </div>
          <VideoCard />
        </div>
      </div>
    </section>
  );
}

/* ============================================================ Industries */
function Industries() {
  return (
    <section className="section" id="industries">
      <div className="container">
        <div className="section-head">
          <span className="eyebrow"><Bi en="Industries" de="Zielmärkte" /></span>
          <h2 className="h2" style={{ marginTop: 14 }}>
            <Bi en="Built for heterogeneous ERP landscapes" de="Gebaut für heterogene ERP-Landschaften" />
          </h2>
        </div>
        <div className="grid grid-2">
          <div className="card">
            <div className="top-rule" />
            <h3 className="h3"><Bi en="PE-owned industrial portfolios" de="PE-eigene Industrieportfolios" /></h3>
            <p>
              <Bi
                en="Primary market. Heterogeneous ERPs from acquisitions, Operating-Partner teams with an efficiency mandate, short hold periods under EBITDA pressure. Proven in an industrial PE portfolio and built to scale across further holdings."
                de="Primärmarkt. Heterogene ERPs aus Akquisitionen, Operating-Partner-Teams mit Effizienz-Mandat, kurze Halteperioden unter EBITDA-Druck. Bewährt in einem industriellen PE-Portfolio und skalierbar auf weitere Beteiligungen."
              />
            </p>
          </div>
          <div className="card">
            <div className="top-rule" />
            <h3 className="h3"><Bi en="Independent mid-market industry" de="Unabhängiger industrieller Mittelstand" /></h3>
            <p>
              <Bi
                en="Secondary market. €50–500M revenue with SAP R/3, ECC or mixed ERP landscapes. A longer sales motion — but repeatable and independent of the PE cycle."
                de="Sekundärmarkt. €50–500 Mio. Umsatz mit SAP-R/3-, ECC- oder gemischten ERP-Landschaften. Längere Verkaufsmotion — dafür wiederholbar und unabhängig vom PE-Zyklus."
              />
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================================================ Live counter */
// A processed-invoices counter that keeps climbing. The baseline is anchored to
// the real clock at ~perDay/day (so day-to-day it stays consistent and sensible),
// and within a visit it ticks up at a varying cadence so the number feels alive.
function LiveCounter({ base, perDay, anchorISO }) {
  const anchor = new Date(anchorISO).getTime();
  const baseline = () => base + Math.floor(((Date.now() - anchor) / 86400000) * perDay);
  const [val, setVal] = useState(() => Math.max(base, baseline()));
  const valRef = useRef(val);

  useEffect(() => {
    let stopped = false;
    const schedule = () => {
      if (stopped) return;
      const delay = 4000 + Math.random() * 13000; // 4–17s: mal schneller, mal langsamer
      window.setTimeout(() => {
        if (stopped) return;
        const clockTarget = baseline();
        let next = valRef.current;
        if (clockTarget > next) {
          next = clockTarget; // keep in step with the day's expected volume
        } else {
          // gentle live drift so a visitor actually sees it move
          const r = Math.random();
          next += r < 0.55 ? 1 : r < 0.8 ? 2 : 0;
        }
        valRef.current = next;
        setVal(next);
        schedule();
      }, delay);
    };
    schedule();
    return () => { stopped = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <>{val.toLocaleString("de-DE")}</>;
}

/* ============================================================ For PE firms */
function ForPE() {
  return (
    <section className="section section--deep" id="for-pe">
      <div className="container">
        <div className="section-head">
          <span className="eyebrow eyebrow--dark"><Bi en="For PE firms" de="Für PE-Häuser" /></span>
          <h2 className="h2" style={{ marginTop: 14 }}>
            <Bi en="Manage the portfolio from one system" de="Das Portfolio aus einem System steuern" />
          </h2>
          <p>
            <Bi
              en="Live reporting across a multi-entity industrial portfolio where JUMIBI AP runs in production — around 300 invoices a day, climbing in real time."
              de="Live-Reporting über mehrere Entities eines Industrie-Portfolios, in dem JUMIBI AP produktiv läuft — rund 300 Rechnungen pro Tag, in Echtzeit steigend."
            />
          </p>
        </div>

        <div className="kpi-row" style={{ marginBottom: 40 }}>
          <div className="kpi"><div className="v"><LiveCounter base={5645} perDay={300} anchorISO="2026-06-01T00:00:00Z" /></div><div className="l"><Bi en="Invoices processed · 12 mailboxes (live)" de="Rechnungen verarbeitet · 12 Mailboxen (live)" /></div></div>
          <div className="kpi"><div className="v">€55<span className="u">M</span></div><div className="l"><Bi en="Cumulative invoice volume" de="Kumuliertes Volumen" /></div></div>
          <div className="kpi"><div className="v">€19.9<span className="u">M</span></div><div className="l"><Bi en="Fully auto-approved" de="Vollautomatisch genehmigt" /></div></div>
          <div className="kpi"><div className="v">93<span className="u">%</span></div><div className="l"><Bi en="Best-entity completion (Entity North)" de="Beste Entity (Entity North)" /></div></div>
        </div>

        <div className="split">
          <div>
            <h3 className="h3" style={{ color: "#fff" }}><Bi en="Why the ROI compounds" de="Warum sich der ROI summiert" /></h3>
            <p className="on-dark-2" style={{ marginTop: 16, fontSize: 16, lineHeight: 1.7 }}>
              <Bi
                en="Licence consolidation in the seven-figure range. A back-office SSC without years of ERP migration. Roll-out in 2–4 weeks per site, not eight quarters. And portfolio-wide transparency on a daily basis — no calls to local management."
                de="Lizenzkonsolidierung im siebenstelligen Bereich. Ein Backoffice-SSC ohne jahrelange ERP-Migration. Roll-out in 2–4 Wochen pro Standort, nicht acht Quartale. Und portfolio-weite Transparenz auf täglicher Basis — ohne Anruf beim lokalen Management."
              />
            </p>
            <a href="/#book" className="btn btn--primary" style={{ marginTop: 28 }}>
              <Bi en="Start an ROI conversation" de="ROI-Gespräch starten" />
            </a>
          </div>
          <div className="pe-board">
            <div className="pe-board__bar">
              <span className="layer-label"><Bi en="Cross-entity risk overview" de="Cross-Entity-Risikoübersicht" /></span>
              <span className="pe-board__conf"><Bi en="Example" de="Beispiel" /></span>
            </div>
            <table className="risk-table">
              <thead>
                <tr>
                  <th><Bi en="Entity" de="Entity" /></th>
                  <th className="num"><Bi en="Inv." de="Rechn." /></th>
                  <th className="num"><Bi en="At risk" de="Risiko €" /></th>
                  <th className="num"><Bi en="Compl." de="Quote" /></th>
                  <th><Bi en="Status" de="Status" /></th>
                </tr>
              </thead>
              <tbody>
                <tr><td>Entity North · RS</td><td className="num">1,022</td><td className="num">€0</td><td className="num">93%</td><td><span className="risk risk--ok">On track</span></td></tr>
                <tr><td>Entity West · DE</td><td className="num">820</td><td className="num">€4.2M</td><td className="num">61%</td><td><span className="risk risk--warn"><Bi en="Elevated" de="Erhöht" /></span></td></tr>
                <tr><td>Entity Atlantic · US</td><td className="num">540</td><td className="num">€3.3M</td><td className="num">0%</td><td><span className="risk risk--crit">Critical</span></td></tr>
              </tbody>
            </table>
            <div className="pe-reco">
              <div className="pe-reco__top">
                <span className="risk risk--crit">Critical</span>
                <span className="pe-reco__ent">Entity Atlantic · US</span>
              </div>
              <div className="pe-reco__title">
                <Bi en="Processing halted — 540 invoices not started" de="Verarbeitung gestoppt — 540 Rechnungen nicht begonnen" />
              </div>
              <p>
                <Bi
                  en="0% completion across all 540 invoices (€3.3M at risk). Reassign approver capacity from Entity North this week and trigger a catch-up run to clear the not-started backlog."
                  de="0% Abschluss über alle 540 Rechnungen (€3,3 Mio. Risiko). Approver-Kapazität diese Woche von Entity North umschichten und einen Catch-up-Lauf starten, um den Not-started-Backlog aufzulösen."
                />
              </p>
              <div className="pe-reco__by">
                — <Bi en="Recommendation generated by JUMIBI AI Business Suite" de="Empfehlung generiert von JUMIBI AI Business Suite" />
              </div>
            </div>
            <p className="pe-board__note">
              <Bi en="Illustrative example · anonymized data" de="Illustratives Beispiel · anonymisierte Daten" />
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================================================ Testimonials */
function Testimonials() {
  return (
    <section className="section section--ice">
      <div className="container">
        <div className="section-head">
          <span className="eyebrow"><Bi en="In their words" de="Kundenstimmen" /></span>
          <h2 className="h2" style={{ marginTop: 14 }}>
            <Bi en="Operators, not slideware" de="Operatoren, kein Folien-Marketing" />
          </h2>
        </div>
        <div className="grid grid-2">
          <div className="testimonial">
            <h3 className="h3"><Bi en="“A game-changer for our IT and business efficiency”" de="„Ein Gamechanger für unsere IT- und Geschäftseffizienz“" /></h3>
            <blockquote>
              <Bi
                en="We outsourced our entire IT to JUMIBI — an amazing job from start to finish. The service is outstanding, and the response time is impressively fast."
                de="Wir haben unsere gesamte IT an JUMIBI ausgelagert — von Anfang bis Ende hervorragend. Der Service ist erstklassig und die Reaktionszeit beeindruckend schnell."
              />
            </blockquote>
            <div className="byline">
              <div className="avatar"><img src={`${process.env.PUBLIC_URL}/images/consol-susanne.png`} alt="Susanne Feldhausen" /></div>
              <div className="who"><b>Susanne Feldhausen</b><span>CEO, ConSol Dortmund</span></div>
            </div>
          </div>
          <div className="testimonial">
            <h3 className="h3"><Bi en="“We unified invoicing across four locations”" de="„Rechnungsverarbeitung über vier Standorte vereinheitlicht“" /></h3>
            <blockquote>
              <Bi
                en="With JUMIBI AP we unified our invoice process across four locations and replaced scattered tools with one centralized platform."
                de="Mit JUMIBI AP haben wir unseren Rechnungsprozess über vier Standorte vereinheitlicht und verstreute Tools durch eine zentrale Plattform ersetzt."
              />
            </blockquote>
            <div className="byline">
              <div className="avatar"><img src={`${process.env.PUBLIC_URL}/images/paul.jpg`} alt="Paul Robin" /></div>
              <div className="who"><b>Paul Robin</b><span>Senior Manager Finance, Matikon</span></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================================================ CTA panel */
function CTA() {
  return (
    <section className="section--sm" style={{ padding: "32px 0 96px" }}>
      <div className="container">
        <div className="cta-panel">
          <h2 className="h2"><Bi en="30 minutes. The live system in action." de="30 Minuten. Das Live-System in Aktion." /></h2>
          <p>
            <Bi
              en="No transformation theatre — we show you the running platform and the numbers. Roll-out per site: 2–4 weeks, integrated over your ERP API."
              de="Kein Transformations-Theater — wir zeigen Ihnen die laufende Plattform und die Zahlen. Roll-out pro Standort: 2–4 Wochen, integriert über Ihre ERP-API."
            />
          </p>
          <div className="hero-cta">
            <a href="/#book" className="btn btn--primary"><Bi en="Pick a slot" de="Termin wählen" /></a>
            <a href="/#book" className="btn btn--outline"><Bi en="See available dates →" de="Verfügbare Termine ansehen →" /></a>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================================================ Footer */
function Footer() {
  return (
    <footer className="site-footer" role="contentinfo">
      <div className="container">
        <div className="footer-grid">
          <div className="f-brand">
            <Link to="/" className="brand" aria-label="JUMIBI">
              <PyramidMark />
              <span className="wm">JUMIBI</span>
            </Link>
            <p className="philosophy">Existing systems, new capabilities.</p>
            <p className="tagline-de"><Bi en="Consolidate processes, not ERPs." de="Konsolidiert Prozesse, nicht ERPs." /></p>
          </div>
          <div>
            <h4>Platform</h4>
            <ul>
              <li><a href="/#platform"><Bi en="Value stack" de="Wertstufen" /></a></li>
              <li><Link to="/capabilities">Capabilities</Link></li>
              <li><a href="/#industries"><Bi en="Industries" de="Industries" /></a></li>
              <li><a href="/#for-pe"><Bi en="For PE firms" de="Für PE-Häuser" /></a></li>
            </ul>
          </div>
          <div>
            <h4>Capabilities</h4>
            <ul>
              <li><Link to="/capabilities#ap">JUMIBI AP</Link></li>
              <li><Link to="/capabilities#capture">JUMIBI Capture</Link></li>
              <li><Link to="/capabilities#einvoicing">JUMIBI E-Invoicing</Link></li>
              <li><Link to="/capabilities#archive">JUMIBI Archive</Link></li>
              <li><Link to="/capabilities#order-intake">JUMIBI Order Intake</Link></li>
            </ul>
          </div>
          <div>
            <h4><Bi en="Get in touch" de="Kontakt" /></h4>
            <address>
              JUMIBI Consulting UG (haftungsbeschränkt)<br />
              Stuppstr. 16, 50823 Köln<br />
              <a href="mailto:info@jumibi.de?subject=JUMIBI%20Demo%20Request">info@jumibi.de</a><br />
              <a href="/#book"><Bi en="Book a demo →" de="Demo buchen →" /></a>
            </address>
          </div>
        </div>
        <div className="footer-legal">
          <p>© 2026 JUMIBI Consulting UG (haftungsbeschränkt) · <Bi en="All rights reserved." de="Alle Rechte vorbehalten." /></p>
          <p>Managing Director: Mike Mannz · Stuppstr. 16, 50823 Cologne, Germany · Local Court of Cologne (Amtsgericht Köln), HRB 111198 · USt-IdNr. DE355246446 · www.jumibi.de</p>
        </div>
      </div>
    </footer>
  );
}

/* ============================================================ Scroll-next button */
function ScrollNext() {
  const scrollToNext = () => {
    const sections = Array.from(document.querySelectorAll("section"));
    const y = window.scrollY + window.innerHeight / 2;
    const next = sections.find((s) => s.getBoundingClientRect().top + window.scrollY > y);
    if (next) next.scrollIntoView({ behavior: "smooth", block: "start" });
    else window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
  };
  return (
    <button className="scroll-next" aria-label="Scroll to next section" onClick={scrollToNext}>
      ↓
    </button>
  );
}

/* ============================================================ Home page */
function HomePage() {
  return (
    <main>
      <Hero />
      <TrustBar />
      <PlatformStack />
      <HomeCapabilities />
      <APVideo />
      <Industries />
      <ForPE />
      <Testimonials />
      <BookingSection />
      <CTA />
    </main>
  );
}

/* ============================================================ App */
export default function App() {
  const [, setConsent] = useState(() => localStorage.getItem("clarity-consent") === "true");
  const [showBanner, setShowBanner] = useState(() => localStorage.getItem("clarity-consent") === null);

  const handleAccept = () => { setConsent(true); localStorage.setItem("clarity-consent", "true"); setShowBanner(false); };
  const handleDecline = () => { setConsent(false); localStorage.setItem("clarity-consent", "false"); setShowBanner(false); };

  return (
    <LangProvider>
      <Router>
        <ScrollToTop />
        <HashScrollFix />
        <HeadMeta />
        <Clarity />
        <JsonLd />
        <Header />
        <ScrollNext />
        {showBanner && <ConsentBanner onAccept={handleAccept} onDecline={handleDecline} />}
        <Routes>
          <Route path="/" element={<><HomePage /><Footer /></>} />
          <Route path="/capabilities" element={<CapabilitiesPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/services-page" element={<ServicesPage />} />
        </Routes>
      </Router>
    </LangProvider>
  );
}
